import React, { useEffect, useRef, useState } from 'react';

/**
 * Live countdown for the storefront announcement banner, e.g. "00H:27M:37S".
 * The end time comes from the admin (Announcements -> "Countdown timer"), nothing is hardcoded.
 * When the time runs out it calls onExpire once (the banner then hides / refetches).
 */
interface Props {
  endsAt: string | null | undefined;
  onExpire?: () => void;
  className?: string;
}

const pad = (n: number) => String(n).padStart(2, '0');

const AnnouncementCountdown: React.FC<Props> = ({ endsAt, onExpire, className }) => {
  const endMs = endsAt ? new Date(endsAt).getTime() : NaN;
  const onExpireRef = useRef(onExpire);
  onExpireRef.current = onExpire;
  const [left, setLeft] = useState(() => (Number.isNaN(endMs) ? 0 : Math.max(0, endMs - Date.now())));

  useEffect(() => {
    if (Number.isNaN(endMs)) return;
    let expired = false;
    const tick = () => {
      const remaining = Math.max(0, endMs - Date.now());
      setLeft(remaining);
      if (remaining === 0 && !expired) {
        expired = true;
        onExpireRef.current?.();
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endMs]);

  if (Number.isNaN(endMs) || left <= 0) return null;

  const total = Math.floor(left / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;

  return (
    <span className={className} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
      <svg width="1.1em" height="1.1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {days > 0 ? `${days}D:` : ''}{pad(hours)}H:{pad(minutes)}M:{pad(seconds)}S
    </span>
  );
};

export default AnnouncementCountdown;